<template>
  <div class="dashboard-customize-drawer">
    <button
      type="button"
      class="dashboard-customize-drawer__backdrop"
      :aria-label="$t('dashboardCustomization.close')"
      @click="$emit('close')"
    ></button>

    <aside
      class="dashboard-customize-drawer__panel"
      :aria-label="$t('dashboardCustomization.title')"
    >
      <header class="dashboard-customize-drawer__header">
        <div>
          <h2>{{ $t('dashboardCustomization.title') }}</h2>
          <p>{{ $t('dashboardCustomization.description') }}</p>
        </div>
        <button
          type="button"
          class="dashboard-customize-drawer__close"
          :aria-label="$t('dashboardCustomization.close')"
          @click="$emit('close')"
        >
          <i class="f7-icons" aria-hidden="true">multiply</i>
        </button>
      </header>

      <div class="dashboard-customize-drawer__body">
        <button
          type="button"
          class="dashboard-customize-drawer__restore"
          @click="confirmRestoreDefault"
        >
          {{ $t('dashboardCustomization.restoreDefault') }}
        </button>

        <section class="dashboard-customize-drawer__section">
          <h3>{{ $t('dashboardCustomization.activeWidgets') }}</h3>
          <p
            v-if="!activeWidgets.length"
            class="dashboard-customize-drawer__empty"
          >
            {{ $t('dashboardCustomization.noActiveWidgets') }}
          </p>
          <DashboardWidgetConfigCard
            v-for="widget in activeWidgets"
            :key="widget.id"
            :widget="widget"
            :can-move-up="canMoveWidgetUp(widget)"
            :can-move-down="canMoveWidgetDown(widget)"
            @toggle-widget="$emit('toggle-widget', widget.id)"
            @move-widget-up="$emit('move-widget-up', widget.id)"
            @move-widget-down="$emit('move-widget-down', widget.id)"
            @preview-widget-hover="
              $emit('preview-widget-hover', widget.id)
            "
            @preview-widget-leave="
              $emit('preview-widget-leave', widget.id)
            "
          />
        </section>

        <section class="dashboard-customize-drawer__section">
          <h3>{{ $t('dashboardCustomization.hiddenWidgets') }}</h3>
          <p
            v-if="!hiddenWidgets.length"
            class="dashboard-customize-drawer__empty"
          >
            {{ $t('dashboardCustomization.noHiddenWidgets') }}
          </p>
          <DashboardWidgetConfigCard
            v-for="widget in hiddenWidgets"
            :key="widget.id"
            :widget="widget"
            @toggle-widget="$emit('toggle-widget', widget.id)"
          />
        </section>

        <section
          v-if="unavailableWidgets.length"
          class="dashboard-customize-drawer__section"
        >
          <h3>
            {{ $t('dashboardCustomization.unavailableWidgets') }}
          </h3>
          <DashboardWidgetConfigCard
            v-for="widget in unavailableWidgets"
            :key="widget.id"
            :widget="widget"
            locked
          />
        </section>
      </div>
    </aside>
  </div>
</template>

<script>
import WidgetPreview from '@/components/Dashboard/WidgetPreview/index.vue';

const DashboardWidgetConfigCard = {
  name: 'DashboardWidgetConfigCard',
  components: {
    WidgetPreview,
  },
  props: {
    widget: {
      type: Object,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    canMoveUp: {
      type: Boolean,
      default: false,
    },
    canMoveDown: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isUnavailable() {
      return this.locked || !this.widget.available;
    },
    isToggleDisabled() {
      return this.isUnavailable || this.widget.required;
    },
    isOrderDisabled() {
      return this.isUnavailable || this.widget.orderLocked;
    },
    toggleText() {
      if (this.isUnavailable) {
        return this.$t('dashboardCustomization.locked');
      }

      if (this.widget.required) {
        return this.$t('dashboardCustomization.required');
      }

      return this.widget.enabled
        ? this.$t('dashboardCustomization.active')
        : this.$t('dashboardCustomization.hidden');
    },
    toggleAriaLabel() {
      if (this.isToggleDisabled) {
        return this.toggleText;
      }

      return this.widget.enabled
        ? this.$t('dashboardCustomization.hideWidget')
        : this.$t('dashboardCustomization.activateWidget');
    },
    toggleTooltip() {
      if (this.isToggleDisabled) {
        return '';
      }

      return this.widget.enabled
        ? this.$t('dashboardCustomization.hideWidgetTooltip')
        : this.$t('dashboardCustomization.activateWidgetTooltip');
    },
    translatedTitle() {
      if (this.widget.titleKey) {
        return this.$t(this.widget.titleKey);
      }

      return this.widget.title;
    },
    translatedDescription() {
      if (this.widget.descriptionKey) {
        return this.$t(this.widget.descriptionKey);
      }

      return this.widget.description;
    },
  },
  template: `
    <article
      class="dashboard-widget-config-card"
      :class="{
        'dashboard-widget-config-card--locked': isUnavailable,
      }"
    >
      <WidgetPreview
        :preview-type="widget.previewType"
        :locked="isUnavailable"
      />

      <div class="dashboard-widget-config-card__content">
        <div class="dashboard-widget-config-card__title-row">
          <h4>{{ translatedTitle }}</h4>
          <span
            v-if="widget.required || widget.orderLocked"
            class="dashboard-widget-config-card__badge"
          >
            {{ $t('dashboardCustomization.fixed') }}
          </span>
        </div>
        <p>{{ translatedDescription }}</p>
        <p
          v-if="isUnavailable && widget.reason"
          class="dashboard-widget-config-card__reason"
        >
          {{ widget.reason }}
        </p>

        <div class="dashboard-widget-config-card__actions">
          <button
            type="button"
            class="dashboard-widget-config-card__toggle"
            :class="[
              {
                'dashboard-widget-config-card__toggle--active':
                  widget.enabled && !isUnavailable,
                'dashboard-widget-config-card__control--with-tooltip':
                  !isToggleDisabled,
              },
            ]"
            :disabled="isToggleDisabled"
            :aria-label="toggleAriaLabel"
            :data-tooltip="toggleTooltip"
            @click="$emit('toggle-widget')"
          >
            {{ toggleText }}
          </button>

          <div
            v-if="widget.enabled && !isUnavailable"
            class="dashboard-widget-config-card__order"
            @mouseenter="$emit('preview-widget-hover')"
            @mouseleave="$emit('preview-widget-leave')"
          >
            <button
              type="button"
              class="dashboard-widget-config-card__control--with-tooltip"
              :aria-label="$t('dashboardCustomization.moveUp')"
              :data-tooltip="$t('dashboardCustomization.moveUpTooltip')"
              :disabled="isOrderDisabled || !canMoveUp"
              @click="$emit('move-widget-up')"
            >
              <i class="f7-icons" aria-hidden="true">arrow_up</i>
            </button>
            <button
              type="button"
              class="dashboard-widget-config-card__control--with-tooltip"
              :aria-label="$t('dashboardCustomization.moveDown')"
              :data-tooltip="$t('dashboardCustomization.moveDownTooltip')"
              :disabled="isOrderDisabled || !canMoveDown"
              @click="$emit('move-widget-down')"
            >
              <i class="f7-icons" aria-hidden="true">arrow_down</i>
            </button>
          </div>
        </div>
      </div>
    </article>
  `,
};

export default {
  name: 'CustomizeDashboardDrawer',
  components: {
    DashboardWidgetConfigCard,
  },
  props: {
    widgets: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    // Estas propiedades computadas filtran los widgets en función de su estado de disponibilidad y habilitación, clasificándolos en activos, ocultos y no disponibles para su visualización en la interfaz de personalización del panel de control.
    activeWidgets() {
      return this.widgets.filter(
        (widget) => widget.available && widget.enabled
      );
    },
    reorderableActiveWidgets() {
      return this.activeWidgets.filter(
        (widget) => !widget.orderLocked
      );
    },
    lockedActiveWidgets() {
      return this.activeWidgets.filter(
        (widget) => widget.orderLocked
      );
    },
    rowLockedActiveWidgets() {
      return this.activeWidgets.filter((widget) => widget.rowLock);
    },
    hasThreeActiveWidgetsWithTwoRowLocks() {
      return (
        this.activeWidgets.length === 3 &&
        this.rowLockedActiveWidgets.length === 2
      );
    },
    hasSingleReorderableWidgetWithFixedWidgets() {
      return (
        this.reorderableActiveWidgets.length === 1 &&
        this.lockedActiveWidgets.length > 0
      );
    },
    // Los widgets ocultos son aquellos que están disponibles pero no habilitados, lo que significa que el usuario ha decidido no mostrarlos en su panel de control, pero aún pueden ser activados si lo desea.
    hiddenWidgets() {
      return this.widgets.filter(
        (widget) => widget.available && !widget.enabled
      );
    },
    // Los widgets no disponibles son aquellos que no están disponibles para su uso, lo que puede deberse a diversas razones como dependencias no cumplidas, restricciones de permisos o incompatibilidades, y se muestran en una sección separada para informar al usuario de su estado.
    unavailableWidgets() {
      return this.widgets.filter((widget) => !widget.available);
    },
  },
  methods: {
    getAllowedIndexesForRowLock(widget) {
      if (widget.rowLock === 'first') {
        return [0, 1];
      }

      if (widget.rowLock === 'second') {
        return this.activeWidgets.length === 3 &&
          this.lockedActiveWidgets.length === 1
          ? [1, 2]
          : [2, 3];
      }

      return null;
    },
    isRowLockedMoveAllowed(widget, targetIndex) {
      const allowedIndexes = this.getAllowedIndexesForRowLock(widget);

      return !allowedIndexes || allowedIndexes.includes(targetIndex);
    },
    canMoveWidgetUp(widget) {
      if (widget.orderLocked) {
        return false;
      }

      if (
        this.hasThreeActiveWidgetsWithTwoRowLocks &&
        widget.rowLock
      ) {
        return false;
      }

      const widgetIndex = this.activeWidgets.findIndex(
        (activeWidget) => activeWidget.id === widget.id
      );

      if (
        widgetIndex <= 0 ||
        !this.isRowLockedMoveAllowed(widget, widgetIndex - 1)
      ) {
        return false;
      }

      if (this.hasSingleReorderableWidgetWithFixedWidgets) {
        if (this.lockedActiveWidgets.length === 1) {
          return (
            Number(widget.order) >
            Number(this.lockedActiveWidgets[0].order)
          );
        }

        return Number(widget.order) > 0;
      }

      return (
        this.reorderableActiveWidgets.findIndex(
          (activeWidget) => activeWidget.id === widget.id
        ) > 0
      );
    },
    canMoveWidgetDown(widget) {
      if (widget.orderLocked) {
        return false;
      }

      if (
        this.hasThreeActiveWidgetsWithTwoRowLocks &&
        widget.rowLock
      ) {
        return false;
      }

      const activeWidgetIndex = this.activeWidgets.findIndex(
        (activeWidget) => activeWidget.id === widget.id
      );

      if (
        activeWidgetIndex === -1 ||
        activeWidgetIndex >= this.activeWidgets.length - 1 ||
        !this.isRowLockedMoveAllowed(widget, activeWidgetIndex + 1)
      ) {
        return false;
      }

      if (this.hasSingleReorderableWidgetWithFixedWidgets) {
        if (this.lockedActiveWidgets.length === 1) {
          return (
            Number(widget.order) <
            Number(this.lockedActiveWidgets[0].order)
          );
        }

        return Number(widget.order) < 40;
      }

      const widgetIndex = this.reorderableActiveWidgets.findIndex(
        (activeWidget) => activeWidget.id === widget.id
      );

      return (
        widgetIndex >= 0 &&
        widgetIndex < this.reorderableActiveWidgets.length - 1
      );
    },
    confirmRestoreDefault() {
      this.$f7.dialog.confirm(
        this.$t('dashboardCustomization.restoreConfirmMessage'),
        this.$t('dashboardCustomization.restoreConfirmTitle'),
        () => {
          this.$emit('restore-default');
        }
      );
    },
  },
};
</script>

<style lang="scss">
@import './style.scss';
</style>
